import { isValidElement, useEffect, useMemo, useState } from "react";
import { BsWrenchAdjustable } from "react-icons/bs";
import { FiBarChart2, FiBookOpen, FiShield, FiUsers, FiX } from "react-icons/fi";
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdLocalShipping, MdOutlineLogout } from "react-icons/md";
import { RiMoneyDollarCircleLine, RiSteering2Line } from "react-icons/ri";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../store/Slices/AuthSlice";
import "../styles/SideMenu.css";
import { getResolvedUserSidebar, isSuperAdminRole, permissionsMatch, sideMenuList } from "../utils/helper";
import { images } from "../utils/images";
import {
  collapsableIcon,
  contractSidebarIcon,
  accessManagementIcon,
  fuelSidebarIcon,
  tripSidebarIcon,
  inventoryIcon,
} from "../utils/svgs";
import LogoutModal from "./modals/LogoutModal";

const driverSafetySidebarIcon = <RiSteering2Line fontSize={"24px"} />;
const financeSidebarIcon = <RiMoneyDollarCircleLine fontSize={"24px"} />;

const getSidebarFilesBaseUrl = () => {
  const rawFilesUrl = String(process.env.REACT_APP_API_FILES_URL || "").trim();
  if (rawFilesUrl) return rawFilesUrl;

  const rawBaseUrl = String(process.env.REACT_APP_API_BASE_URL || "").trim().replace(/\/api\/?$/, "");
  return rawBaseUrl;
};

const DEFAULT_SIDEBAR_ICON_FILTER = "brightness(0) invert(1)";
const ACTIVE_SIDEBAR_ICON_FILTER =
  "brightness(0) saturate(100%) invert(22%) sepia(69%) saturate(2848%) hue-rotate(214deg) brightness(86%) contrast(88%)";
const SHOW_3PL_SIDEBAR = false;
const SHOW_CONTRACT_SIDEBAR = true;
// Trips List is the v2 trip listing and is now integrated into the Trips page (Listing tab),
// so it stays hidden from the sidebar.
const SHOW_TRIPS_LIST_SIDEBAR = false;
const TRIPS_LIST_SIDEBAR_SLUG = "trips-list";

const isDirectAssetUrl = (value) => /^(https?:)?\/\//i.test(String(value || "").trim());
const isSpecialAssetUrl = (value) => /^(blob:|data:)/i.test(String(value || "").trim());

const getSidebarIconSrc = (value) => {
  const rawValue = String(value || "")
    .trim()
    .replace(/\\/g, "/");

  if (!rawValue || rawValue === "null" || rawValue === "undefined") return "";
  if (isDirectAssetUrl(rawValue) || isSpecialAssetUrl(rawValue)) return rawValue;

  const baseUrl = getSidebarFilesBaseUrl();
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  if (!normalizedBase) return rawValue.replace(/^\/+/, "");

  if (rawValue.startsWith(normalizedBase)) return rawValue;

  return `${normalizedBase}/${rawValue.replace(/^\/+/, "")}`;
};

const getSidebarIconFilter = (isActive) =>
  isActive ? ACTIVE_SIDEBAR_ICON_FILTER : DEFAULT_SIDEBAR_ICON_FILTER;

const normalizeSidebarSlug = (value) =>
  String(value || "")
    .trim()
    .replace(/^\/+/, "")
    .toLowerCase()
    .replace(/[&]/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const isTripsListSidebarEntry = (value) =>
  normalizeSidebarSlug(value) === normalizeSidebarSlug(TRIPS_LIST_SIDEBAR_SLUG);

const is3PlSidebarEntry = (value) => {
  const normalized = normalizeSidebarSlug(value);
  if (!normalized) return false;
  return (
    normalized === "3pl" ||
    normalized === "three-pl" ||
    normalized.startsWith("3pl-") ||
    normalized.startsWith("three-pl-")
  );
};

// The super-admin sidebar renders the static sideMenuList instead of the permission
// driven one, so the Trips List and 3PL flags have to be applied to both sources.
const visibleSideMenuList = (sideMenuList || [])
  .filter((item) => {
    if (!SHOW_3PL_SIDEBAR && is3PlSidebarEntry(item?.heading || item?.slug || item?.url || item?.name)) {
      return false;
    }
    return true;
  })
  .map((item) => {
    const subMenus = Array.isArray(item?.subMenusList) ? item.subMenusList : null;

    if (!subMenus) {
      if (!SHOW_TRIPS_LIST_SIDEBAR && isTripsListSidebarEntry(item?.url || item?.slug)) {
        return null;
      }
      return item;
    }

    const remainingSubMenus = subMenus.filter((subMenu) => {
      if (!SHOW_TRIPS_LIST_SIDEBAR && isTripsListSidebarEntry(subMenu?.url || subMenu?.slug)) {
        return false;
      }
      if (!SHOW_3PL_SIDEBAR && is3PlSidebarEntry(subMenu?.url || subMenu?.slug)) {
        return false;
      }
      return true;
    });

    return remainingSubMenus.length === subMenus.length
      ? item
      : { ...item, subMenusList: remainingSubMenus };
  })
  .filter(Boolean);

const customerSidebarIcon = <FiUsers fontSize={"24px"} />;

const trainingLibraryMenuItem = {
  id: "training-library",
  name: "Training Library",
  heading: "Training Library",
  icon: <FiBookOpen fontSize="20px" />,
  slug: "/training-library",
  url: "/training-library",
  isDropdown: true,
  isSingleItem: false,
  menus: [
    { name: "Driver Training", slug: "/training-library/driver-training", url: "/training-library/driver-training" },
    { name: "FMS Training", slug: "/training-library/fms-training", url: "/training-library/fms-training" },
    { name: "FMS Documentation", slug: "/training-library/fms-documentation", url: "/training-library/fms-documentation" },
  ],
  subMenusList: [
    { name: "Driver Training", slug: "/training-library/driver-training", url: "/training-library/driver-training" },
    { name: "FMS Training", slug: "/training-library/fms-training", url: "/training-library/fms-training" },
    { name: "FMS Documentation", slug: "/training-library/fms-documentation", url: "/training-library/fms-documentation" },
  ],
};

const isContractSlugOrName = (value) => {
  const normalized = normalizeSidebarSlug(value);
  if (!normalized) return false;
  return (
    normalized === "contract" ||
    normalized === "contracts" ||
    normalized === "pricing-templates" ||
    normalized === "pricing-template" ||
    normalized === "finance-pricing-templates" ||
    normalized.includes("contract")
  );
};

const getFallbackSidebarIcon = (normalizedSlug, isContract) => {
  if (isContract) return contractSidebarIcon;
  if (normalizedSlug === "maintenance") return <BsWrenchAdjustable fontSize="22px" />;
  if (normalizedSlug === "assets-management" || normalizedSlug === "assets" || normalizedSlug.includes("asset")) return inventoryIcon;
  if (normalizedSlug === "inventory-management" || normalizedSlug === "inventory" || normalizedSlug.includes("inventory")) return inventoryIcon;
  if (normalizedSlug === "trip-management" || normalizedSlug === "trips" || normalizedSlug === "trip" || normalizedSlug.includes("trip")) return tripSidebarIcon;
  if (normalizedSlug === "customers") return customerSidebarIcon;
  if (normalizedSlug === "fmcr" || normalizedSlug === "fleet-monitoring") return <FiBarChart2 fontSize="22px" />;
  if (normalizedSlug === "drivers" || normalizedSlug === "driver-safety") return driverSafetySidebarIcon;
  if (normalizedSlug === "finance") return financeSidebarIcon;
  if (normalizedSlug === "fuel" || normalizedSlug === "fuel-management") return fuelSidebarIcon;
  if (normalizedSlug === "contract") return contractSidebarIcon;
  if (normalizedSlug === "role-management" || normalizedSlug === "roles") return <FiShield fontSize="22px" />;
  if (normalizedSlug === "access-management") return accessManagementIcon;
  if (normalizedSlug === "3pl" || normalizedSlug === "three-pl") return <MdLocalShipping fontSize="22px" />;
  return inventoryIcon;
};

const renderSidebarIcon = (module, hasActiveMenu, isCollapsed = false) => {
  const normalized = normalizeSidebarSlug(module?.slug || module?.name || module?.url || module?.heading);
  const isContract = isContractSlugOrName(normalized);
  const fallbackIcon = getFallbackSidebarIcon(normalized, isContract);

  // 1. If module has an uploaded string icon (file path or full URL)
  if (typeof module?.icon === "string" && module.icon.trim() && module.icon !== "null" && module.icon !== "undefined") {
    const src = getSidebarIconSrc(module.icon);
    return (
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <span className="sidebar_fallback_icon" style={{ display: "inline-flex", alignItems: "center" }}>
          {fallbackIcon}
        </span>
        <img
          src={src}
          alt={module.name || module.heading || "Module icon"}
          style={{
            width: "22px",
            height: "22px",
            objectFit: "contain",
            filter: isCollapsed ? DEFAULT_SIDEBAR_ICON_FILTER : getSidebarIconFilter(hasActiveMenu),
            flexShrink: 0,
            display: "none",
          }}
          onLoad={(event) => {
            event.currentTarget.style.display = "inline-flex";
            const sibling = event.currentTarget.previousSibling;
            if (sibling) sibling.style.display = "none";
          }}
          onError={(event) => {
            event.currentTarget.style.display = "none";
            const sibling = event.currentTarget.previousSibling;
            if (sibling) sibling.style.display = "inline-flex";
          }}
        />
      </span>
    );
  }

  // 2. If module has a React Element icon
  if (isValidElement(module?.icon)) {
    return module.icon;
  }

  // 3. Known fallback icon
  return fallbackIcon;
};

const SideMenu = ({ collapse, setCollapse, onNavigateMobile }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));
  const userSidebar = getResolvedUserSidebar(userDetails?.user_sidebar);

  const hasPermissionInMenu = (menu, permissionSlug = "view") => {
    return permissionsMatch(menu?.permissions, permissionSlug);
  };

  const splitVendorAndWarehouseMenu = (menu) => {
    if (!menu) return [];

    const slug = menu.slug?.startsWith("/") ? menu.slug : `/${menu.slug || ""}`;
    if (slug !== "/vendor-&-warehouse") return [menu];

    const splitMenus = [];
    if (permissionsMatch(menu?.permissions, ["view-vendor", "vendor"])) {
      splitMenus.push({
        ...menu,
        name: "Vendor",
        slug: "/vendor",
      });
    }
    if (permissionsMatch(menu?.permissions, ["view-warehouse", "warehouse"])) {
      splitMenus.push({
        ...menu,
        name: "Warehouse",
        slug: "/warehouse",
      });
    }

    return splitMenus;
  };

  const normalizedUserSidebar = (userSidebar || []).map((module) => ({
    ...module,
    menus: (module?.menus || [])
      .flatMap(splitVendorAndWarehouseMenu)
      .filter((menu) => permissionsMatch(menu?.permissions, "view")),
  }));

  const isCustomerMenu = (menu) => {
    const menuSlug = menu?.slug || menu?.url || menu?.name;
    const normalizedMenuSlug = normalizeSidebarSlug(menuSlug);

    return (
      normalizedMenuSlug === normalizeSidebarSlug("/customers") ||
      normalizedMenuSlug === normalizeSidebarSlug("/3pl/customers") ||
      normalizedMenuSlug === normalizeSidebarSlug("customers")
    );
  };

  const isPricingTemplatesMenu = (menu) => {
    const menuSlug = menu?.slug || menu?.url || menu?.name;
    const normalizedMenuSlug = normalizeSidebarSlug(menuSlug);

    return (
      normalizedMenuSlug === normalizeSidebarSlug("/contract") ||
      normalizedMenuSlug === normalizeSidebarSlug("/pricing-templates") ||
      normalizedMenuSlug === normalizeSidebarSlug("pricing-template") ||
      normalizedMenuSlug === normalizeSidebarSlug("finance-pricing-templates")
    );
  };

  const baseDisplayUserSidebar = (normalizedUserSidebar || []).flatMap((module) => {
    const moduleSlug = module?.slug || module?.name;
    const normalizedModuleSlug = normalizeSidebarSlug(moduleSlug);
    const moduleMenus = Array.isArray(module?.menus) ? module.menus : [];

    if (normalizedModuleSlug === normalizeSidebarSlug("operations")) {
      const driverSafetyMenu = moduleMenus.find((menu) => {
        const menuSlug = menu?.slug || menu?.url || menu?.name;
        return normalizeSidebarSlug(menuSlug) === normalizeSidebarSlug("/driver-safety");
      });

      if (driverSafetyMenu && permissionsMatch(driverSafetyMenu?.permissions, "view")) {
        const driverMenuName = /driver\s*(&|and)?\s*safety/i.test(
          String(driverSafetyMenu.name || "")
        )
          ? "Drivers"
          : driverSafetyMenu.name || "Drivers";

        return [
          {
            ...module,
            id: `single-${module.id}`,
            name: driverMenuName,
            slug: driverSafetyMenu.slug || driverSafetyMenu.url || "/driver-safety",
            url: driverSafetyMenu.slug || driverSafetyMenu.url || "/driver-safety",
            permissions: Array.isArray(driverSafetyMenu?.permissions)
              ? [...driverSafetyMenu.permissions]
              : [],
            menus: [],
            isSingleItem: true,
          },
        ];
      }
      return [];
    }

    if (normalizedModuleSlug === normalizeSidebarSlug("customers")) {
      const customerMenu = moduleMenus.find(isCustomerMenu) || moduleMenus[0];

      if (customerMenu && permissionsMatch(customerMenu?.permissions, "view")) {
        return [
          {
            ...module,
            id: `single-customer-module-${module.id}`,
            name: customerMenu.name || module.name || "Customers",
            slug: "/customers",
            url: "/customers",
            icon: module.icon || customerSidebarIcon,
            permissions: Array.isArray(customerMenu?.permissions)
              ? [...customerMenu.permissions]
              : [],
            menus: [],
            isSingleItem: true,
          },
        ];
      }
      return [];
    }

    if (
      normalizedModuleSlug === normalizeSidebarSlug("fmcr") ||
      moduleMenus.some((m) => normalizeSidebarSlug(m?.slug || m?.url || m?.name) === "fmcr")
    ) {
      const fmcrMenu = moduleMenus.find((m) => normalizeSidebarSlug(m?.slug || m?.url || m?.name) === "fmcr") || moduleMenus[0];
      const permissions = Array.isArray(fmcrMenu?.permissions) && fmcrMenu.permissions.length > 0
        ? fmcrMenu.permissions
        : Array.isArray(module?.permissions)
          ? module.permissions
          : [];

      if (
        fmcrMenu &&
        (isSuperAdminRole(userDetails?.my_role) ||
          permissionsMatch(permissions, [
            "view-tracking",
            "view-report",
            "create-report",
            "view",
            "tracking",
            "reports",
            "track-selected-vehicles",
            "see-full-details",
          ]))
      ) {
        const subMenus = [
          {
            id: "fmcr-vehicle-tracking-sub",
            name: "Vehicle Tracking",
            slug: "/fmcr/vehicle-tracking",
            url: "/fmcr/vehicle-tracking",
          },
        ];

        return [
          {
            ...module,
            id: `fmcr-module-${module.id || "fmcr"}`,
            name: "FMCR",
            heading: "FMCR",
            slug: "/fmcr",
            url: "/fmcr",
            icon: module.icon || <FiBarChart2 fontSize="22px" />,
            permissions,
            isDropdown: true,
            isSingleItem: false,
            menus: subMenus,
            subMenusList: subMenus,
          },
        ];
      }
      return [];
    }

    if (is3PlSidebarEntry(normalizedModuleSlug)) {
      const customerMenu = moduleMenus.find(isCustomerMenu);
      const remainingMenus = moduleMenus.filter((menu) => !isCustomerMenu(menu));
      const nextModules = [];

      if (customerMenu && permissionsMatch(customerMenu?.permissions, "view")) {
        nextModules.push({
          ...module,
          id: `single-customer-${module.id}`,
          name: customerMenu.name || "Customers",
          slug: "/customers",
          url: "/customers",
          icon: customerSidebarIcon,
          permissions: Array.isArray(customerMenu?.permissions)
            ? [...customerMenu.permissions]
            : [],
          menus: [],
          isSingleItem: true,
        });
      }

      // Temporarily hide 3PL from sidebar. Set SHOW_3PL_SIDEBAR to true to show it again.
      if (SHOW_3PL_SIDEBAR && remainingMenus.length) {
        nextModules.push({
          ...module,
          menus: remainingMenus,
        });
      }

      return nextModules;
    }

    const isContractSlugOrName = (value) => {
      const normalized = normalizeSidebarSlug(value);
      if (!normalized) return false;
      return (
        normalized === "contract" ||
        normalized === "contracts" ||
        normalized === "pricing-templates" ||
        normalized === "pricing-template" ||
        normalized === "finance-pricing-templates" ||
        normalized.includes("contract")
      );
    };

    if (
      isContractSlugOrName(normalizedModuleSlug) ||
      moduleMenus.some((menu) => isContractSlugOrName(menu?.slug || menu?.url || menu?.name))
    ) {
      const contractMenu = moduleMenus.find((m) => isContractSlugOrName(m?.slug || m?.url || m?.name)) || moduleMenus[0];
      const permissions = Array.isArray(contractMenu?.permissions) && contractMenu.permissions.length > 0
        ? contractMenu.permissions
        : Array.isArray(module?.permissions)
          ? module.permissions
          : [];

      if (contractMenu && permissionsMatch(permissions, "view")) {
        const rawIcon = module?.icon || contractMenu?.icon;
        const iconToUse = (isValidElement(rawIcon) ? rawIcon : null) || (rawIcon && rawIcon !== "null" && rawIcon !== "undefined" ? rawIcon : contractSidebarIcon);

        return [
          {
            ...module,
            id: `single-contract-module-${module.id}`,
            name: "Contract",
            slug: "/contract",
            url: "/contract",
            icon: iconToUse,
            permissions,
            menus: [],
            isSingleItem: true,
          },
        ];
      }
      return [];
    }


    if (
      normalizedModuleSlug === "trip-management" ||
      normalizedModuleSlug === "trips" ||
      normalizedModuleSlug === "trip"
    ) {
      const tripsMenu = moduleMenus.find((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return mSlug === "trips" || mSlug === "trip" || mSlug === "trips-list" || mSlug === "tripslist" || mSlug === "trip-list" || mSlug === "triplist";
      });
      const hasTripsView = Boolean(
        tripsMenu && permissionsMatch(tripsMenu.permissions, "view")
      );

      const pettyCashMenu = moduleMenus.find((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return mSlug === "petty-cash" || mSlug === "pettycash";
      });
      const hasPettyCashView = Boolean(
        pettyCashMenu &&
        (permissionsMatch(pettyCashMenu.permissions, "view") ||
         permissionsMatch(pettyCashMenu.permissions, [
           "petty-cash-view",
           "petty-cash-approval-status",
           "petty-cash-status-update",
           "status-update",
           "petty-cash-export-pdf",
           "petty-cash-export-excel",
         ]))
      );

      const tripExpenseMenu = moduleMenus.find((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return (
          mSlug === "trip-expense" ||
          mSlug === "trip-expenses" ||
          mSlug === "expenses" ||
          mSlug === "tripexpenses"
        );
      });
      const hasTripExpenseView = Boolean(
        tripExpenseMenu &&
        (permissionsMatch(tripExpenseMenu.permissions, "view") ||
         permissionsMatch(tripExpenseMenu.permissions, [
           "expenses-view",
           "trip-expenses-view",
           "trip-expense-view",
           "trip-expense-export-pdf",
           "trip-expense-export-excel",
         ]))
      );

      const subMenus = [
        ...(hasTripsView ? [{
          id: "trips-sub",
          name: "Trips",
          slug: "/trips",
          url: "/trips",
        }] : []),
        ...(hasPettyCashView ? [{
          id: "petty-cash-sub",
          name: "Petty Cash",
          slug: "/trips/petty-cash",
          url: "/trips/petty-cash",
        }] : []),
        ...(hasTripExpenseView ? [{
          id: "trip-expense-sub",
          name: "Trip Expense",
          slug: "/trips/expenses",
          url: "/trips/expenses",
        }] : []),
      ];

      if (subMenus.length > 0 || permissionsMatch(module?.permissions, "view")) {
        return [
          {
            ...module,
            id: `trip-module-${module.id || 'main'}`,
            name: "Trip Management",
            heading: "Trip Management",
            slug: "/trips",
            url: "/trips",
            icon: module.icon || tripSidebarIcon,
            isDropdown: true,
            isSingleItem: false,
            menus: subMenus,
            subMenusList: subMenus,
          }
        ];
      }
      return [];
    }

    if (
      normalizedModuleSlug === "fuel-management" ||
      normalizedModuleSlug === "fuel"
    ) {
      const hasFuelView = moduleMenus.some((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return (mSlug === "fuel" || mSlug === "/fuel") && permissionsMatch(m?.permissions, "view");
      });
      const hasFuelStationsView = moduleMenus.some((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return (mSlug === "fuel-stations" || mSlug === "fuel-station" || mSlug === "/fuel-stations") && permissionsMatch(m?.permissions, "view");
      });
      const hasFuelRequestView = moduleMenus.some((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return (mSlug === "fuel-request" || mSlug === "fuel-requests" || mSlug === "fuel-requests-sub") && permissionsMatch(m?.permissions, "view");
      }) || (userSidebar || []).some((mod) => (mod?.menus || []).some((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return (mSlug === "fuel-request" || mSlug === "fuel-requests") && permissionsMatch(m?.permissions, "view");
      }));

      const hasAnyFuelView = hasFuelView || hasFuelStationsView || hasFuelRequestView || permissionsMatch(module?.permissions, "view") || moduleMenus.some((m) => permissionsMatch(m?.permissions, "view"));

      if (hasAnyFuelView) {
        const subMenus = [
          ...(hasFuelView || (!hasFuelStationsView && !hasFuelRequestView) ? [{
            id: "fuel-sub",
            name: "Fuel",
            slug: "/fuel",
            url: "/fuel",
          }] : []),
          ...(hasFuelStationsView ? [{
            id: "fuel-stations-sub",
            name: "Fuel Stations",
            slug: "/fuel-stations",
            url: "/fuel-stations",
          }] : []),
          ...(hasFuelRequestView ? [{
            id: "fuel-requests-sub",
            name: "Fuel Requests",
            slug: "/fuel-requests",
            url: "/fuel-requests",
          }] : []),
        ];

        return [
          {
            ...module,
            id: `fuel-module-${module.id || 'main'}`,
            name: "Fuel Management",
            heading: "Fuel Management",
            slug: "/fuel",
            url: "/fuel",
            icon: module.icon || fuelSidebarIcon,
            isDropdown: true,
            isSingleItem: false,
            menus: subMenus,
            subMenusList: subMenus,
          }
        ];
      }
      return [];
    }

    return [module];
  });

  const pricingTemplatesMenus = [];
  const displayUserSidebarWithoutContract = baseDisplayUserSidebar
    .map((module) => {
      const moduleSlug = module?.slug || module?.name;
      const normalizedModuleSlug = normalizeSidebarSlug(moduleSlug);

      if (module?.isSingleItem || normalizedModuleSlug !== normalizeSidebarSlug("finance")) {
        return module;
      }

      const moduleMenus = Array.isArray(module?.menus) ? module.menus : [];
      const remainingMenus = moduleMenus.filter((menu) => {
        const isPricingTemplates = isPricingTemplatesMenu(menu);
        const menuSlug = normalizeSidebarSlug(menu?.slug || menu?.url || menu?.name);
        if (menuSlug === "fmcr" || menuSlug === "fleet-monitoring") return false;

        if (isPricingTemplates) {
          if (isSuperAdminRole(userDetails?.my_role) || permissionsMatch(menu?.permissions, "view")) {
            pricingTemplatesMenus.push({
              ...menu,
              icon: menu.icon || module.icon,
            });
          }
        }

        return !isPricingTemplates;
      });

      return {
        ...module,
        menus: remainingMenus,
      };
    })
    .map((module) => {
      if (SHOW_TRIPS_LIST_SIDEBAR) return module;

      if (module?.isSingleItem) {
        return isTripsListSidebarEntry(module?.slug || module?.url) ? null : module;
      }

      const moduleMenus = Array.isArray(module?.menus) ? module.menus : [];
      const remainingMenus = moduleMenus.filter((menu) => !isTripsListSidebarEntry(menu?.slug || menu?.url));

      return remainingMenus.length === moduleMenus.length
        ? module
        : { ...module, menus: remainingMenus };
    })
    .filter(Boolean)
    .filter((module) => {
      if (!SHOW_3PL_SIDEBAR && is3PlSidebarEntry(module?.slug || module?.name || module?.url)) {
        return false;
      }
      return true;
    })
    .filter((module) => module?.isSingleItem || module?.url || module?.menus?.length);

  const displayUserSidebar = (() => {
    // Temporarily hide Contract from sidebar. Set SHOW_CONTRACT_SIDEBAR to true to show it again.
    if (!SHOW_CONTRACT_SIDEBAR || !pricingTemplatesMenus.length) return displayUserSidebarWithoutContract;
    let sidebarList = displayUserSidebarWithoutContract;
    if (pricingTemplatesMenus.length) {
      const hasContractItem = displayUserSidebarWithoutContract.some((module) => {
        if (!module?.isSingleItem) return false;
        return isPricingTemplatesMenu(module);
      });

      if (!hasContractItem) {
        const contractItem = {
          ...pricingTemplatesMenus[0],
          id: `single-contract-${pricingTemplatesMenus[0]?.id || "contract"}`,
          name: "Contract",
          slug: "/contract",
          url: "/contract",
          icon: pricingTemplatesMenus[0]?.icon || contractSidebarIcon,
          menus: [],
          isSingleItem: true,
        };
        const customerIndex = displayUserSidebarWithoutContract.findIndex((module) => {
          const moduleSlug = module?.slug || module?.url || module?.name;
          return normalizeSidebarSlug(moduleSlug) === normalizeSidebarSlug("customers");
        });
        const insertIndex = customerIndex >= 0 ? customerIndex + 1 : displayUserSidebarWithoutContract.length;

        sidebarList = [
          ...displayUserSidebarWithoutContract.slice(0, insertIndex),
          contractItem,
          ...displayUserSidebarWithoutContract.slice(insertIndex),
        ];
      }
    }

    const hasTripItem = sidebarList.some((module) => {
      const moduleSlug = module?.slug || module?.url || module?.name;
      return normalizeSidebarSlug(moduleSlug).includes("trip");
    });

    if (!hasTripItem) {
      const userHasTripAccess = (userSidebar || []).some((mod) => {
        const modSlug = normalizeSidebarSlug(mod?.slug || mod?.name);
        if (modSlug.includes("trip")) {
          return permissionsMatch(mod?.permissions, "view") || (mod?.menus || []).some((m) => permissionsMatch(m?.permissions, "view"));
        }
        return false;
      });

      if (userHasTripAccess) {
        const tripItem = {
          id: "trip-management-module",
          name: "Trip Management",
          heading: "Trip Management",
          slug: "/trips",
          url: "/trips",
          icon: tripSidebarIcon,
          isDropdown: true,
          isSingleItem: false,
          menus: [
            {
              id: "trips-sub",
              name: "Trips",
              slug: "/trips",
              url: "/trips",
            },
          ],
          subMenusList: [
            {
              id: "trips-sub",
              name: "Trips",
              slug: "/trips",
              url: "/trips",
            },
          ],
        };

        const assetIndex = sidebarList.findIndex((module) => {
          const moduleSlug = module?.slug || module?.name;
          return normalizeSidebarSlug(moduleSlug).includes("asset");
        });

        if (assetIndex !== -1) {
          sidebarList = [
            ...sidebarList.slice(0, assetIndex + 1),
            tripItem,
            ...sidebarList.slice(assetIndex + 1),
          ];
        } else {
          sidebarList = [tripItem, ...sidebarList];
        }
      }
    }

    const hasFuelItem = sidebarList.some((module) => {
      const moduleSlug = module?.slug || module?.url || module?.name;
      return normalizeSidebarSlug(moduleSlug).includes("fuel");
    });

    if (!hasFuelItem) {
      const hasFuelView = (userSidebar || []).some((mod) => {
        const modSlug = normalizeSidebarSlug(mod?.slug || mod?.name);
        if (modSlug.includes("fuel")) {
          return (mod?.menus || []).some((m) => {
            const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
            return (mSlug === "fuel" || mSlug === "/fuel") && permissionsMatch(m?.permissions, "view");
          });
        }
        return false;
      });

      const hasFuelStationsView = (userSidebar || []).some((mod) => {
        const modSlug = normalizeSidebarSlug(mod?.slug || mod?.name);
        if (modSlug.includes("fuel")) {
          return (mod?.menus || []).some((m) => {
            const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
            return (mSlug === "fuel-stations" || mSlug === "fuel-station" || mSlug === "/fuel-stations") && permissionsMatch(m?.permissions, "view");
          });
        }
        return false;
      });

      const hasFuelRequestView = (userSidebar || []).some((mod) => (mod?.menus || []).some((m) => {
        const mSlug = normalizeSidebarSlug(m?.slug || m?.url || m?.name);
        return (mSlug === "fuel-request" || mSlug === "fuel-requests") && permissionsMatch(m?.permissions, "view");
      }));

      const userHasFuelAccess = hasFuelView || hasFuelStationsView || hasFuelRequestView;

      if (userHasFuelAccess) {
        const fallbackSubMenus = [
          ...(hasFuelView || (!hasFuelStationsView && !hasFuelRequestView) ? [{ id: "fuel-sub", name: "Fuel", slug: "/fuel", url: "/fuel" }] : []),
          ...(hasFuelStationsView ? [{ id: "fuel-stations-sub", name: "Fuel Stations", slug: "/fuel-stations", url: "/fuel-stations" }] : []),
          ...(hasFuelRequestView ? [{ id: "fuel-requests-sub", name: "Fuel Requests", slug: "/fuel-requests", url: "/fuel-requests" }] : []),
        ];

        const fuelItem = {
          id: "fuel-module-fallback",
          name: "Fuel Management",
          heading: "Fuel Management",
          slug: "/fuel",
          url: "/fuel",
          icon: fuelSidebarIcon,
          isDropdown: true,
          isSingleItem: false,
          menus: fallbackSubMenus,
          subMenusList: fallbackSubMenus,
        };

        const driverIndex = sidebarList.findIndex((module) => {
          const moduleSlug = module?.slug || module?.name;
          return normalizeSidebarSlug(moduleSlug).includes("driver");
        });

        if (driverIndex !== -1) {
          sidebarList = [
            ...sidebarList.slice(0, driverIndex + 1),
            fuelItem,
            ...sidebarList.slice(driverIndex + 1),
          ];
        } else {
          sidebarList = [...sidebarList, fuelItem];
        }
      }
    }

    const hasFmcrItem = sidebarList.some((module) => {
      const moduleSlug = module?.slug || module?.url || module?.name;
      return normalizeSidebarSlug(moduleSlug) === "fmcr";
    });

    if (!hasFmcrItem) {
      const fmcrItem = {
        id: "fmcr-module",
        name: "FMCR",
        slug: "/fmcr",
        url: "/fmcr",
        icon: <FiBarChart2 fontSize="22px" />,
        menus: [],
        isSingleItem: true,
      };

      const tripIndex = sidebarList.findIndex((module) => {
        const moduleSlug = module?.slug || module?.name;
        return normalizeSidebarSlug(moduleSlug).includes("trip");
      });

      if (tripIndex !== -1) {
        sidebarList = [
          ...sidebarList.slice(0, tripIndex + 1),
          fmcrItem,
          ...sidebarList.slice(tripIndex + 1),
        ];
      } else {
        sidebarList = [...sidebarList, fmcrItem];
      }
    }

    const hasRoleManagementItem = sidebarList.some((module) => {
      const moduleSlug = module?.slug || module?.url || module?.name;
      return (
        normalizeSidebarSlug(moduleSlug) === normalizeSidebarSlug("/role-management") ||
        normalizeSidebarSlug(moduleSlug) === "role-management"
      );
    });

    if (!hasRoleManagementItem) {
      const roleManagementItem = {
        id: "role-management-module",
        name: "Role Management",
        slug: "/role-management",
        url: "/role-management",
        icon: <FiShield fontSize="22px" />,
        menus: [],
        isSingleItem: true,
      };

      const accessIndex = sidebarList.findIndex((module) => {
        const moduleSlug = module?.slug || module?.name;
        return normalizeSidebarSlug(moduleSlug).includes("access");
      });

      if (accessIndex !== -1) {
        sidebarList = [
          ...sidebarList.slice(0, accessIndex + 1),
          roleManagementItem,
          ...sidebarList.slice(accessIndex + 1),
        ];
      } else {
        sidebarList = [...sidebarList, roleManagementItem];
      }
    }

    const hasRolesItem = sidebarList.some((module) => {
      const moduleSlug = module?.slug || module?.url || module?.name;
      return (
        normalizeSidebarSlug(moduleSlug) === "roles" ||
        normalizeSidebarSlug(moduleSlug) === "/roles"
      );
    });

    if (!hasRolesItem) {
      const rolesItem = {
        id: "roles-module",
        name: "Roles",
        slug: "/roles",
        url: "/roles",
        icon: accessManagementIcon,
        menus: [],
        isSingleItem: true,
      };

      const roleMgmtIndex = sidebarList.findIndex((module) => {
        const moduleSlug = module?.slug || module?.name;
        return normalizeSidebarSlug(moduleSlug).includes("role-management");
      });

      if (roleMgmtIndex !== -1) {
        sidebarList = [
          ...sidebarList.slice(0, roleMgmtIndex + 1),
          rolesItem,
          ...sidebarList.slice(roleMgmtIndex + 1),
        ];
      } else {
        sidebarList = [...sidebarList, rolesItem];
      }
    }

    return sidebarList;
  })();

  const superAdminSideMenuList = visibleSideMenuList.map((item) => {
    const normalizedHeading = normalizeSidebarSlug(item.heading || item.url);
    const dynamicModule = (userSidebar || []).find((mod) => {
      const modSlug = normalizeSidebarSlug(mod.slug || mod.name || mod.url);
      return (
        modSlug === normalizedHeading ||
        (normalizedHeading.includes("fmcr") && modSlug.includes("fmcr")) ||
        (normalizedHeading.includes("trip") && modSlug.includes("trip")) ||
        (normalizedHeading.includes("finance") && modSlug.includes("finance")) ||
        (normalizedHeading.includes("role") && modSlug.includes("role")) ||
        (normalizedHeading.includes("access") && modSlug.includes("access"))
      );
    });

    const dynamicIcon = dynamicModule?.icon;
    const finalIcon = (typeof dynamicIcon === "string" && dynamicIcon.trim() && dynamicIcon !== "null" && dynamicIcon !== "undefined")
      ? dynamicIcon
      : item.icon;

    return {
      ...item,
      id: `sa-mod-${item.id}`,
      name: item.heading,
      icon: finalIcon,
      isSingleItem: !item.isDropdown,
      menus: item.subMenusList || [],
    };
  });

  const baseSidebarList = isSuperAdminRole(userDetails?.my_role)
    ? superAdminSideMenuList
    : (displayUserSidebar && displayUserSidebar.length > 0 ? displayUserSidebar : superAdminSideMenuList);

  const activeSidebarList = useMemo(() => {
    const list = baseSidebarList || [];
    const hasTraining = list.some((item) => {
      const slug = item?.slug || item?.url || item?.name || item?.heading;
      return normalizeSidebarSlug(slug).includes("training");
    });
    return hasTraining ? list : [...list, trainingLibraryMenuItem];
  }, [baseSidebarList]);

  const isMenuItemActive = (menu) => {
    const slug = menu.slug || menu.url;
    if (!slug) return false;

    // Normalize paths and slugs to start with /
    const activePath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const activeSlug = slug.startsWith('/') ? slug : `/${slug}`;

    // Ensure /fmcr/vehicle-tracking (and /fmcr/vehicle_tracking) activates Vehicle Tracking, not Fleet Monitoring
    const isVehicleTrackingPath =
      activePath.startsWith('/fmcr/vehicle-tracking') ||
      activePath.startsWith('/fmcr/vehicle_tracking') ||
      activePath.startsWith('/vehicle-tracking') ||
      activePath.startsWith('/vehicle_tracking');

    if (isVehicleTrackingPath) {
      if (
        activeSlug === '/fmcr/vehicle-tracking' ||
        activeSlug === '/fmcr/vehicle_tracking' ||
        activeSlug === '/vehicle-tracking' ||
        activeSlug === '/vehicle_tracking'
      ) return true;
      if (activeSlug === '/fmcr' || activeSlug === '/fmcr/fleet-monitoring') return false;
    }

    // Ensure /fmcr/vehicle-status activates Vehicle Status, not Fleet Monitoring
    const isVehicleStatusPath =
      activePath.startsWith('/fmcr/vehicle-status') ||
      activePath.startsWith('/fmcr/vehicle_status') ||
      activePath.startsWith('/vehicle-status') ||
      activePath.startsWith('/vehicle_status');

    if (isVehicleStatusPath) {
      if (
        activeSlug === '/fmcr/vehicle-status' ||
        activeSlug === '/fmcr/vehicle_status' ||
        activeSlug === '/vehicle-status' ||
        activeSlug === '/vehicle_status'
      ) return true;
      if (activeSlug === '/fmcr' || activeSlug === '/fmcr/fleet-monitoring') return false;
    }

    // Ensure Fleet Monitoring (/fmcr) only activates for Fleet Monitoring root/reports, NOT other sub-routes
    if (activeSlug === '/fmcr' || activeSlug === '/fmcr/fleet-monitoring') {
      if (
        activePath === '/fmcr' ||
        activePath === '/fmcr/' ||
        activePath === '/fmcr/fleet-monitoring' ||
        activePath.startsWith('/fmcr/generate-new-report') ||
        activePath.startsWith('/fmcr/report-details')
      ) return true;
      return false;
    }

    // Ensure /trips/fuel-requests or /fuel-requests activates Fuel Request, not /trips or /fuel
    if (activePath.startsWith('/trips/fuel-requests') || activePath.startsWith('/fuel-requests')) {
      if (activeSlug === '/trips/fuel-requests' || activeSlug === '/fuel-requests') return true;
      if (activeSlug === '/trips' || activeSlug === '/fuel') return false;
    }

    // Ensure /trips/petty-cash activates Petty Cash, not /trips
    if (activePath.startsWith('/trips/petty-cash') || activePath.startsWith('/petty-cash')) {
      if (activeSlug === '/trips/petty-cash' || activeSlug === '/petty-cash') return true;
      if (activeSlug === '/trips') return false;
    }

    // Ensure /trips/expenses activates Trip Expense, not /trips
    if (
      activePath.startsWith('/trips/expenses') ||
      activePath.startsWith('/trips/trip-expenses') ||
      activePath.startsWith('/trip-expenses')
    ) {
      if (
        activeSlug === '/trips/expenses' ||
        activeSlug === '/trips/trip-expenses' ||
        activeSlug === '/trip-expenses'
      ) return true;
      if (activeSlug === '/trips') return false;
    }

    // Ensure /trips-list and all trip stage detail pages (e.g. /trips-list/in-transit/:id, /trips-list/planning/:id) activate Trips
    if (activePath.startsWith('/trips-list') || activePath.startsWith('/new-trips')) {
      if (activeSlug === '/trips' || activeSlug === '/trips-list') return true;
    }

    // Exact match or sub-resource match
    if (activePath === activeSlug || activePath.startsWith(activeSlug + '/')) return true;

    // Special mappings for specific modules
    if (
      (activeSlug === '/assets' || activeSlug === '/assets-registration' || activeSlug.includes('asset')) &&
      (activePath === '/assets' || activePath.startsWith('/assets/') || activePath.startsWith('/assets-registration'))
    ) return true;
    if (
      activeSlug === '/vendor-&-warehouse' &&
      (
        activePath === '/vendor' ||
        activePath.startsWith('/vendor/') ||
        activePath === '/warehouse' ||
        activePath.startsWith('/warehouse/')
      )
    ) return true;
    if (activeSlug === '/procurement' && (activePath.includes('rfqs') || activePath.includes('po/'))) return true;
    if (
      activeSlug === "/3pl/transporter" &&
      (activePath === "/transporters" || activePath.startsWith("/transporters/"))
    ) {
      return true;
    }
    if (
      activeSlug === "/3pl/customers" &&
      (activePath === "/customers" || activePath.startsWith("/customers/"))
    ) {
      return true;
    }

    return false;
  }

  // Initialize openMenus based on active route
  const [openMenus, setOpenMenus] = useState(() => {
    const initialState = {};

    if (activeSidebarList) {
      activeSidebarList.forEach((item, idx) => {
        const subMenus = item.menus || item.subMenusList || [];
        if (!item?.isSingleItem && subMenus.some(sub => isMenuItemActive(sub))) {
          initialState[`user-mod-${item.id || idx}`] = true;
        }
      });
    }

    if (trainingLibraryMenuItem.subMenusList.some(sub => isMenuItemActive(sub))) {
      initialState[trainingLibraryMenuItem.id] = true;
    }

    return initialState;
  });

  const [hoveredMenuId, setHoveredMenuId] = useState(null);



  const activeTab = (item) => {
    const slug = item.slug || item.url;
    if (!slug) return "";
    const cleanPath = pathname.split('/')[1] || pathname.split('/')[0];
    const cleanSlug = slug.startsWith('/') ? slug.slice(1) : slug;
    return cleanPath === cleanSlug.split('/')[0] ? "active" : "";
  };

  const isSubMenuActive = (subMenus) => {
    return subMenus?.some(sub => isMenuItemActive(sub));
  };

  const toggleMenu = (id, defaultOpen = false) => {
    setOpenMenus((prev) => {
      const current = prev[id] !== undefined ? prev[id] : defaultOpen;
      return {
        ...prev,
        [id]: !current,
      };
    });
  };

  const getModuleTargetUrl = (module) => {
    if (module.isSingleItem || (!module.menus?.length && !module.subMenusList?.length)) {
      const raw = module.url || module.slug || "";
      return raw.startsWith("/") ? raw : `/${raw}`;
    }
    const firstSub = (module.menus && module.menus[0]) || (module.subMenusList && module.subMenusList[0]);
    const raw = firstSub?.slug || firstSub?.url || module.url || module.slug || "";
    return raw.startsWith("/") ? raw : `/${raw}`;
  };

  const getSidebarTargetUrl = (sidebar) => {
    if (!sidebar.isDropdown || !sidebar.subMenusList?.length) {
      const raw = sidebar.url || "";
      return raw.startsWith("/") ? raw : `/${raw}`;
    }
    const firstSub = sidebar.subMenusList[0];
    const raw = firstSub?.url || sidebar.url || "";
    return raw.startsWith("/") ? raw : `/${raw}`;
  };

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleOpenLogoutModal = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    dispatch(logout());
    localStorage.clear();
    navigate('/');
  };

  return (
    <>
      <div
        id="mainLogoWhite"
        className={collapse ? "collapsed_logo_box" : ""}
        onClick={() => {
          if (collapse) setCollapse(false);
        }}
        title={collapse ? "Open Sidebar" : ""}
      >
        <div className="main_logo_img_wrapper">
          <img
            id="mainLogoImg"
            src={images.ajclLogo}
            alt="Logo"
          />
          {collapse && (
            <span className="collapsed_hover_expand_icon">
              {collapsableIcon}
            </span>
          )}
        </div>
        {
          !collapse &&
          <div className="sidebar_user_profile_info">
            <h3 title={userDetails?.username || "User"}>{userDetails ? userDetails.username : "User"}</h3>
            <p title={userDetails?.email || "Email"}>{userDetails ? userDetails.email : "Email"}</p>
          </div>
        }
        {
          !collapse && (
            <span
              id="collapseHoverIcon"
              className="collapsable_icon"
              onClick={(e) => {
                e.stopPropagation();
                setCollapse(true);
              }}
              title="Close Sidebar"
            >
              {collapsableIcon}
            </span>
          )
        }
        <span
          className="mobile_drawer_close_icon"
          onClick={(e) => {
            e.stopPropagation();
            if (onNavigateMobile) onNavigateMobile();
          }}
          title="Close Mobile Drawer"
        >
          <FiX style={{ fontSize: "24px", color: "#ffffff" }} />
        </span>
      </div>

      <div className="side_menu_list">
        {activeSidebarList.map((module, i) => {
          const isSingleItem = Boolean(module?.isSingleItem || (!module?.menus?.length && !module?.subMenusList?.length));
          const subMenus = module.menus || module.subMenusList || [];
          const hasActiveMenu = isSingleItem
            ? isMenuItemActive(module)
            : isSubMenuActive(subMenus);

          return (
            <div
              key={i}
              className={`side_menu_item_container ${isSingleItem ? "single_item" : "dropdown_item"}`}
              onMouseEnter={() => {
                if (collapse && !isSingleItem) setHoveredMenuId(`user-mod-${module.id || i}`);
              }}
              onMouseLeave={() => {
                if (collapse && !isSingleItem) setHoveredMenuId(null);
              }}
            >
              {isSingleItem ? (
                <div className={activeTab(module)}>
                  <Link
                    to={getModuleTargetUrl(module)}
                    className={collapse ? "collapsed_icon" : ""}
                    onClick={() => {
                      if (onNavigateMobile) onNavigateMobile();
                    }}
                  >
                    {renderSidebarIcon(module, hasActiveMenu, collapse)}
                    {!collapse && <span style={{ lineHeight: "0" }}>{module.name || module.heading}</span>}
                  </Link>
                </div>
              ) : (
                <>
                  <div
                    className={`menu_header ${collapse ? "collapsed_header" : ""} ${hasActiveMenu ? "active_parent" : ""}`}
                    onClick={() => {
                      if (collapse) {
                        setCollapse(false);
                        setOpenMenus({ [`user-mod-${module.id || i}`]: true });
                        if (!hasActiveMenu) {
                          const firstSub = subMenus[0];
                          if (firstSub) {
                            const raw = firstSub.slug || firstSub.url || "";
                            const targetPath = raw.startsWith("/") ? raw : `/${raw}`;
                            navigate(targetPath);
                            if (onNavigateMobile) onNavigateMobile();
                          }
                        }
                      } else {
                        toggleMenu(`user-mod-${module.id || i}`, hasActiveMenu);
                      }
                    }}
                    title={collapse ? (module.name || module.heading) : ""}
                  >
                    <div className="menu_header_left">
                      {renderSidebarIcon(module, hasActiveMenu, collapse)}
                      {!collapse && <span>{module.name || module.heading}</span>}
                    </div>
                    {!collapse && (
                      <span className="arrow_icon">
                        {(openMenus[`user-mod-${module.id || i}`] ?? hasActiveMenu) ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                      </span>
                    )}
                  </div>

                  {!collapse && (openMenus[`user-mod-${module.id || i}`] ?? hasActiveMenu) && (
                    <div className="submenu_list">
                      {subMenus.map((menu, j) => (
                        <Link
                          to={(menu.slug || menu.url)?.startsWith("/") ? (menu.slug || menu.url) : `/${menu.slug || menu.url}`}
                          key={j}
                          className={`submenu_item ${isMenuItemActive(menu) ? "active_sub" : ""}`}
                          title={menu.name}
                          onClick={() => {
                            if (onNavigateMobile) onNavigateMobile();
                          }}
                        >
                          <span>{menu.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}

                  {collapse && hoveredMenuId === `user-mod-${module.id || i}` && (
                    <div className="sidebar_flyout_popover">
                      <div className="flyout_header">{module.name || module.heading}</div>
                      <div className="flyout_menu_list">
                        {subMenus.map((menu, j) => (
                          <Link
                            to={(menu.slug || menu.url)?.startsWith("/") ? (menu.slug || menu.url) : `/${menu.slug || menu.url}`}
                            key={j}
                            className={`flyout_menu_item ${isMenuItemActive(menu) ? "active_flyout" : ""}`}
                            onClick={() => {
                              setHoveredMenuId(null);
                              if (onNavigateMobile) onNavigateMobile();
                            }}
                          >
                            <span>{menu.name}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Pinned Bottom Logout Container */}
      <div className="sidebar_bottom_container">
        <div
          onClick={handleOpenLogoutModal}
          className={`sidebar_logout_btn ${collapse ? "collapsed_logout" : ""}`}
          title={collapse ? "Logout" : ""}
        >
          <div className="sidebar_logout_icon_wrapper">
            <MdOutlineLogout />
          </div>
          {!collapse && <span className="sidebar_logout_text">Logout</span>}
        </div>
      </div>

      <LogoutModal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onLogout={handleConfirmLogout}
      />
    </>
  );
};

export default SideMenu;
